import { useParams, Link, Navigate } from "react-router-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import CountUp from "react-countup";
import { Check, ArrowLeft, Sparkles } from "lucide-react";
import { getApp, APPS } from "../data/apps";
import { useStats } from "../hooks/useStats";
import { Reveal, AppStoreButtons } from "../components/Shared";
import { AppIcon } from "../components/AppIcon";
import { PhoneCarousel } from "../components/PhoneCarousel";
import { FeatureExplorer } from "../components/FeatureExplorer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function AppDetail() {
  const { slug } = useParams();
  const app = getApp(slug);
  const { stats } = useStats(5000);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  if (!app) return <Navigate to="/applications" replace />;

  const appStats = stats?.apps?.[app.slug] || {};
  const others = APPS.filter((a) => a.slug !== app.slug);

  return (
    <main data-testid={`app-detail-${app.slug}`}>
      {/* Hero */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center overflow-hidden">
        <motion.img
          src={app.heroImage}
          style={{ y: bgY }}
          alt={`${app.name} — ${app.category}`}
          className="absolute inset-0 h-[118%] w-full object-cover blur-[2px]"
        />
        <div className="absolute inset-0 bg-navy/72" />
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(120% 90% at 15% 20%, ${app.accent}33 0%, transparent 55%)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-navy/30" />

        <div className="relative z-10 mx-auto max-w-7xl w-full px-5 sm:px-8 pt-28 pb-16 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <Link
              to="/applications"
              data-testid="back-to-apps"
              className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft size={16} /> Toutes les applications
            </Link>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mt-8"
            >
              <AppIcon app={app} variant="solid" iconSize={32} containerClass="h-16 w-16 rounded-2xl shadow-xl" />
            </motion.div>

            <h1 className="mt-6 font-display font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl drop-shadow-sm">
              {app.name}
            </h1>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 backdrop-blur-md px-3.5 py-1.5 text-sm font-medium text-white">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: app.accent }} />
              {app.category}
            </div>
            <p className="mt-5 max-w-lg text-white/80 leading-relaxed text-lg">{app.tagline}</p>

            <div className="mt-8">
              <AppStoreButtons stores={app.stores} testidPrefix={app.slug} variant="light" />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative flex justify-center lg:justify-end"
          >
            <div className="relative w-full max-w-sm overflow-hidden rounded-[2rem] border border-white/20 shadow-2xl animate-float-slow">
              <img
                src={app.image}
                alt={`Aperçu de l'application ${app.name}`}
                className="w-full h-auto block"
              />
              <div className="pointer-events-none absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-white/40" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Live stats */}
      <section className="bg-navy py-16 text-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50">
            <span className="h-2 w-2 rounded-full bg-africain animate-pulse" /> {app.name} en temps réel
          </div>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {app.stats.map((s, i) => (
              <Reveal key={s.key} i={i}>
                <div data-testid={`detail-stat-${app.slug}-${s.key}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                  <div className="font-display text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums" style={{ color: app.accent }}>
                    <CountUp end={appStats[s.key] ?? 0} duration={2} separator=" " preserveValue />
                  </div>
                  <div className="mt-1 text-xs text-white/50">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fonctionnalités</div>
              <h2 className="mt-4 font-display font-semibold tracking-tight text-navy text-3xl sm:text-4xl">
                Tout ce que {app.name} vous offre
              </h2>
            </div>
          </Reveal>

          <div className="mt-12">
            {app.featureShots ? (
              <FeatureExplorer app={app} />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {app.features.map((f, i) => (
                  <Reveal key={f} i={i}>
                    <div className="flex items-start gap-3 rounded-2xl border border-border p-5">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${app.accent}18`, color: app.accent }}>
                        <Check size={16} />
                      </span>
                      <span className="text-navy font-medium">{f}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* What's new */}
      <section className="py-4 pb-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-3xl border border-border p-8 lg:p-12" style={{ backgroundColor: app.tint }}>
              <div className="flex items-center gap-2" style={{ color: app.accent }}>
                <Sparkles size={18} />
                <span className="text-xs font-semibold uppercase tracking-wider">Nouveautés</span>
              </div>
              <h2 className="mt-4 font-display font-semibold tracking-tight text-navy text-2xl sm:text-3xl">Dernières mises à jour</h2>
              <ul className="mt-6 space-y-4">
                {app.whatsNew.map((n) => (
                  <li key={n} className="flex items-start gap-3 text-navy/80">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: app.accent }} />
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display font-semibold tracking-tight text-navy text-3xl sm:text-4xl text-center">Questions fréquentes</h2>
          </Reveal>
          <Reveal i={1}>
            <Accordion type="single" collapsible className="mt-10">
              {app.faq.map((item, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`} data-testid={`faq-${app.slug}-${idx}`}>
                  <AccordionTrigger className="text-left font-display font-medium text-navy hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Other apps */}
      <section className="border-t border-border py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <h3 className="font-display font-semibold text-navy text-xl mb-6">Découvrez aussi</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            {others.map((a) => {
              return (
                <Link
                  key={a.slug}
                  to={`/applications/${a.slug}`}
                  data-testid={`related-app-${a.slug}`}
                  className="group flex items-center gap-4 rounded-2xl border border-border p-5 hover:-translate-y-1 hover:shadow-lg transition-all"
                >
                  <AppIcon app={a} variant="solid" iconSize={20} containerClass="h-11 w-11 rounded-xl" />
                  <div>
                    <div className="font-display font-semibold text-navy">{a.name}</div>
                    <div className="text-xs text-muted-foreground">{a.category}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
